import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CompleteIcon,
  Cancel as CancelIcon,
  LocalShipping as ShipIcon,
  DoneAll as DeliveredIcon,
} from '@mui/icons-material';
import { formatPrice, resolveOrderCurrency } from '../../../utils/currencyUtils';
import StorePayoutStatus from '../StorePayoutStatus';
import { canMarkDelivered, canMarkShipped, fulfilmentChipColor, isLateShipment, showsFulfilment } from '../../../utils/storeFulfilment';
import { orderStatusLabel, orderStatusColor, completeAwaitsCapture, completeAwaitsEnd } from '../../../utils/orderStatusLabel';
import { TX_TYPE_COLORS, TX_TYPE_KEYS, calculateOrderTotal, serviceOrderAmount } from './orderDisplay';
import { canPerformAction } from './useProviderOrders';
import OrderNotesPanel from './notes/OrderNotesPanel';

const PaymentHistorySection = ({ transactions, currencyCode }) => {
  const { t } = useTranslation();
  if (!transactions || transactions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        {t('orders.noTransactions')}
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
            <TableCell><strong>{t('orders.colDate')}</strong></TableCell>
            <TableCell><strong>{t('orders.txColType')}</strong></TableCell>
            <TableCell><strong>{t('orders.txColAmount')}</strong></TableCell>
            <TableCell><strong>{t('orders.colStatus')}</strong></TableCell>
            <TableCell><strong>{t('orders.txColReference')}</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {transactions.map((tx) => (
            <TableRow key={tx.id}>
              <TableCell>
                {tx.createdAt ? new Date(tx.createdAt).toLocaleString() : t('orders.na')}
              </TableCell>
              <TableCell>
                <Chip
                  label={TX_TYPE_KEYS[tx.type] ? t(TX_TYPE_KEYS[tx.type]) : tx.type}
                  color={TX_TYPE_COLORS[tx.type] || 'default'}
                  size="small"
                />
              </TableCell>
              <TableCell>
                {formatPrice(tx.amount || 0, tx.currency || currencyCode || 'USD')}
              </TableCell>
              <TableCell>{tx.status}</TableCell>
              <TableCell>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  {tx.pspReferenceId || '—'}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const hasValue = (v) => v !== undefined && v !== null && String(v).trim() !== '';

/** A map link for the delivery point; the provider opens it to navigate. */
const mapsUrl = (address) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${address.lat},${address.lng}`)}`;

/**
 * Where, to whom and by when a STORE order is delivered, plus the price breakdown. Everything comes
 * from the server snapshot taken at checkout. Orders placed before delivery existed carry none of
 * it (no address, null fee), so each part renders only when present and an old order shows nothing
 * extra here.
 */
const StoreDeliverySection = ({ order, currencyCode }) => {
  const { t } = useTranslation();
  const address = order.deliveryAddress;
  const hasFee = order.deliveryFee !== undefined && order.deliveryFee !== null;
  if (!address && !hasFee) return null;

  const etaMin = order.etaDaysMin ?? address?.etaDaysMin;
  const etaMax = order.etaDaysMax ?? address?.etaDaysMax;
  const etaRange = hasValue(etaMin) && hasValue(etaMax)
    ? (etaMin === etaMax ? `${etaMin}` : `${etaMin}–${etaMax}`)
    : null;
  const feeIsFree = hasFee && Number(order.deliveryFee) === 0;
  const subtotal = order.itemsSubtotal ?? (hasFee && order.calculatedTotal != null
    ? Number(order.calculatedTotal) - Number(order.deliveryFee)
    : null);

  return (
    <Box data-testid="store-delivery-details" sx={{ mt: 2 }}>
      {address && (
        <>
          <Typography variant="h6" gutterBottom>{t('orders.deliveryTitle')}</Typography>
          <Typography variant="body2">
            {t('orders.deliveryAddress')}: {address.addressLine}
            {hasValue(address.apartment) && `, ${t('orders.deliveryApartment')} ${address.apartment}`}
            {Number.isFinite(address.lat) && Number.isFinite(address.lng) && (
              <>
                {' '}
                <a href={mapsUrl(address)} target="_blank" rel="noopener noreferrer">{t('orders.openInMaps')}</a>
              </>
            )}
          </Typography>
          <Typography variant="body2">
            {t('orders.deliveryContact')}: {address.contactName}
            {hasValue(address.contactPhone) && (
              <>
                {', '}
                <a href={`tel:${address.contactPhone}`} dir="ltr">{address.contactPhone}</a>
              </>
            )}
          </Typography>
          {hasValue(address.notes) && (
            <Typography variant="body2">{t('orders.deliveryNotes')}: {address.notes}</Typography>
          )}
          {etaRange && (
            <Typography variant="body2">
              {t('orders.deliveryEta')}: {t('orders.etaBusinessDays', { range: etaRange })}
            </Typography>
          )}
        </>
      )}
      {hasFee && (
        <Box sx={{ mt: 1.5 }}>
          {subtotal != null && (
            <Typography variant="body2">
              {t('orders.itemsSubtotal')}: {formatPrice(subtotal, currencyCode)}
            </Typography>
          )}
          <Typography variant="body2">
            {t('orders.deliveryFee')}: {feeIsFree ? t('orders.deliveryFree') : formatPrice(order.deliveryFee, currencyCode)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : null);

/** Dates, tracking and the return window of a STORE order, straight from the server's `fulfilment`. */
const StoreFulfilmentSection = ({ order }) => {
  const { t } = useTranslation();
  const f = showsFulfilment(order) ? order.fulfilment : null;
  if (!f) return null;
  const tracking = f.tracking;
  return (
    <Box data-testid="store-fulfilment-details" sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>{t('fulfilment.sectionTitle')}</Typography>
      <Box sx={{ mb: 1 }}>
        <Chip label={t(`fulfilment.status${f.status}`)} color={fulfilmentChipColor(f.status)} size="small" />
        {isLateShipment(order) && <Chip label={t('fulfilment.late')} color="error" size="small" sx={{ ml: 1 }} />}
      </Box>
      {f.paidAt && <Typography variant="body2">{t('fulfilment.paidAt')}: {formatDate(f.paidAt)}</Typography>}
      {f.promisedBy && f.status === 'AWAITING_SHIPMENT' && (
        <Typography variant="body2">{t('fulfilment.promisedBy')}: {formatDate(f.promisedBy)}</Typography>
      )}
      {f.shippedAt && <Typography variant="body2">{t('fulfilment.shippedAt')}: {formatDate(f.shippedAt)}</Typography>}
      {tracking?.carrier && <Typography variant="body2">{t('fulfilment.carrier')}: {tracking.carrier}</Typography>}
      {tracking?.number && (
        <Typography variant="body2">
          {t('fulfilment.trackingNumber')}: <span dir="ltr">{tracking.number}</span>
        </Typography>
      )}
      {f.status === 'SHIPPED' && f.autoDeliverAt && (
        <Typography variant="body2" color="text.secondary">
          {t('fulfilment.autoDeliverAt', { date: formatDate(f.autoDeliverAt) })}
        </Typography>
      )}
      {f.deliveredAt && (
        <Typography variant="body2">
          {t('fulfilment.deliveredAt')}: {formatDate(f.deliveredAt)}
          {f.source ? ` (${t(`fulfilment.source${f.source}`)})` : ''}
        </Typography>
      )}
      {f.returnWindowEndsAt && (
        <Typography variant="body2">
          {t('fulfilment.returnWindowEndsAt')}: {formatDate(f.returnWindowEndsAt)}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Props: open, order, txByOrder, actionLoading, initialTab ('details' | 'notes'),
 * onClose, onComplete(orderId), onShip(order), onDeliver(order), onCancel(order), onMarkPaid(orderId),
 * onNotesChanged().
 */
export default function OrderDetailsDialog({
  open, order, txByOrder, actionLoading, initialTab = 'details',
  onClose, onComplete, onShip, onDeliver, onCancel, onMarkPaid, onNotesChanged,
}) {
  const { t } = useTranslation();
  const selectedOrder = order; // keeps the moved JSX verbatim
  const [tab, setTab] = useState(initialTab);
  useEffect(() => { if (open) setTab(initialTab); }, [open, initialTab, order?.id]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {t('orders.detailsTitle')} #{selectedOrder?.id}
      </DialogTitle>
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="details" label={t('orders.notes.detailsTab')} />
        <Tab value="notes" label={t('orders.notes.tab')} data-testid="order-notes-tab" />
      </Tabs>
      <DialogContent>
        {selectedOrder && tab === 'notes' && (
          <OrderNotesPanel orderId={selectedOrder.id} onChanged={onNotesChanged} autoFocus />
        )}
        {selectedOrder && tab === 'details' && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('orders.customerInfo')}
            </Typography>
            <Typography variant="body2">{t('orders.customerName')}: {selectedOrder.user?.name || t('orders.na')}</Typography>
            <Typography variant="body2">{t('orders.userId')}: {selectedOrder.user?.id || t('orders.na')}</Typography>
            {/* Placeholder until the provider proposes (design §A) — today's date, not a
                date the customer chose. */}
            {selectedOrder.orderType !== 'SERVICE' || selectedOrder.hasProposal ? (
              <Typography variant="body2">{t('orders.requestDate')}: {new Date(selectedOrder.requestDate).toLocaleString()}</Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('orderChat.waitingForYourProposal')}
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            {selectedOrder.orderType === 'STORE' ? (
              <>
                <Typography variant="h6" gutterBottom>
                  {t('orders.storeDetails')}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {t('orders.itemCount', { count: selectedOrder.orderItems?.length || 0 })}
                </Typography>

                {selectedOrder.orderItems && selectedOrder.orderItems.length > 0 && (
                  <List>
                    {selectedOrder.orderItems.map((item, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemText
                          primary={item.storeProduct?.name || t('orders.unknownProduct')}
                          secondary={
                            <Box>
                              <Typography variant="body2">
                                {t('orders.quantity')}: {item.quantity} × {formatPrice(item.unitPrice || 0, resolveOrderCurrency(selectedOrder))}
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {t('orders.total')}: {formatPrice(item.totalPrice || 0, resolveOrderCurrency(selectedOrder))}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}

                <StoreDeliverySection order={selectedOrder} currencyCode={resolveOrderCurrency(selectedOrder)} />

                <Typography variant="h6" sx={{ mt: 2 }}>
                  {t('orders.orderTotal')}: {formatPrice(calculateOrderTotal(selectedOrder), resolveOrderCurrency(selectedOrder))}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="h6" gutterBottom>
                  {t('orders.serviceDetails')}
                </Typography>
                <Typography variant="body2">
                  {t('orders.service')}: {selectedOrder.dogServiceType ? t('services.type' + selectedOrder.dogServiceType, { defaultValue: selectedOrder.dogServiceType.replace(/_/g, ' ') }) : t('orders.na')}
                </Typography>
                <Typography variant="body2">
                  {t('orders.durationDays', { count: selectedOrder.numOfDays || 1 })}
                </Typography>
                <Typography variant="body2">
                  {t('services.price')}: {formatPrice(serviceOrderAmount(selectedOrder), resolveOrderCurrency(selectedOrder))}
                </Typography>

                {selectedOrder.providerDogService && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      {t('orders.serviceDescription')}: {selectedOrder.providerDogService.description || t('orders.na')}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {selectedOrder.moreInfo && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t('orders.additionalNotes')}
                </Typography>
                <Typography variant="body2">{selectedOrder.moreInfo}</Typography>
              </>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('orders.colStatus')}
            </Typography>
            <Chip
              label={orderStatusLabel(selectedOrder, t)}
              color={orderStatusColor(selectedOrder)}
              sx={{ mb: 2 }}
            />
            {selectedOrder.orderType === 'STORE' && <StoreFulfilmentSection order={selectedOrder} />}
            {selectedOrder.orderType === 'STORE' && selectedOrder.payout && (
              <Box data-testid="store-payout-details" sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>{t('payouts.sectionTitle')}</Typography>
                <StorePayoutStatus order={selectedOrder} detailed />
              </Box>
            )}

            {/* Status-specific information */}
            {selectedOrder.orderType === 'SERVICE' ? (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('orders.serviceFlow')}
                </Typography>
                {/* Inquiry stage: nothing is booked yet. Before a proposal the provider owes
                    the customer one; after it, the customer owes an answer. */}
                {(selectedOrder.status === 'ACTIVE' || selectedOrder.status === 'DISCUSSION') && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {selectedOrder.hasProposal
                      ? t('orders.awaitingCustomerAnswer')
                      : t('orders.sendProposalToBook')}
                  </Alert>
                )}
                {/* Capture runs ahead of the service (delta §5): the hold is charged at
                    `capture_at`, shortly before the start. The old copy told the provider
                    to finish the service to get paid, which is the reverse order. */}
                {/* Three MUTUALLY EXCLUSIVE branches. The old "complete after capture"
                    condition was `!== 'CAPTURED'`, which also matched OFFLINE. */}
                {selectedOrder.paymentStatus === 'OFFLINE' &&
                  (selectedOrder.status === 'CONFIRMED' || selectedOrder.status === 'COMPLETED') && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {t('orders.offlineSettlementHint')}
                    <Box sx={{ mt: 1 }}>
                      {selectedOrder.offlinePaidAt ? (
                        <Typography variant="body2">
                          {t('orders.paidOn', { date: new Date(selectedOrder.offlinePaidAt).toLocaleDateString() })}
                        </Typography>
                      ) : (
                        <Button size="small" variant="outlined" disabled={actionLoading}
                          onClick={() => onMarkPaid(selectedOrder.id)}>
                          {t('orders.markAsPaid')}
                        </Button>
                      )}
                    </Box>
                  </Alert>
                )}
                {/* Before the expected end only its reason shows: the capture alerts would
                    contradict a disabled Complete button. */}
                {selectedOrder.status === 'CONFIRMED' && selectedOrder.paymentStatus === 'CAPTURED' && !completeAwaitsEnd(selectedOrder) && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    {t('orders.paymentCapturedMarkComplete')}
                  </Alert>
                )}
                {selectedOrder.status === 'CONFIRMED' && completeAwaitsCapture(selectedOrder) && !completeAwaitsEnd(selectedOrder) && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {t('orders.completeAfterCapture')}
                  </Alert>
                )}
                {completeAwaitsEnd(selectedOrder) && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {t('orders.completeAfterEnd', { date: new Date(selectedOrder.expectedCompletionAt).toLocaleString() })}
                  </Alert>
                )}
              </Box>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('orders.storeFlow')}
                </Typography>
                {canMarkShipped(selectedOrder) && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {t('orders.prepareOrder')}
                  </Alert>
                )}
                {canMarkDelivered(selectedOrder) && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {t('fulfilment.markDeliveredHint')}
                  </Alert>
                )}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('orders.paymentHistory')}
            </Typography>
            <PaymentHistorySection
              transactions={txByOrder[selectedOrder.id]}
              currencyCode={resolveOrderCurrency(selectedOrder)}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {t('common.close')}
        </Button>
        {selectedOrder && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canPerformAction(selectedOrder, 'complete') && (
              <Button
                variant="contained"
                startIcon={<CompleteIcon />}
                onClick={() => onComplete(selectedOrder.id)}
                disabled={actionLoading || completeAwaitsCapture(selectedOrder) || completeAwaitsEnd(selectedOrder)}
                color="success"
              >
                {t('orders.completeButton')}
              </Button>
            )}
            {canPerformAction(selectedOrder, 'markShipped') && (
              <Button
                variant="contained"
                startIcon={<ShipIcon />}
                onClick={() => onShip(selectedOrder)}
                disabled={actionLoading}
                color="primary"
              >
                {t('fulfilment.markShipped')}
              </Button>
            )}
            {canPerformAction(selectedOrder, 'markDelivered') && (
              <Button
                variant="contained"
                startIcon={<DeliveredIcon />}
                onClick={() => onDeliver(selectedOrder)}
                disabled={actionLoading}
                color="success"
              >
                {t('fulfilment.markDelivered')}
              </Button>
            )}
            {canPerformAction(selectedOrder, 'cancel') && (
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => onCancel(selectedOrder)}
                disabled={actionLoading}
                color="error"
              >
                {t('orders.cancelOrder')}
              </Button>
            )}
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
}
